import { useCallback, useEffect, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { namedOperations } from '@gonfalon/observability-graphql/operations';

/**
 * Refetches the conversation's artifact queries when a run ends, so the PR badge and Artifacts tab
 * pick up rows the agent wrote mid-run instead of sitting on their first, usually empty, result.
 *
 * Watches the `isAnalyzing` true -> false edge because a run can end in five different places, and
 * refetches by operation name so every mounted reader refreshes without wiring up a signal.
 *
 * Returns the same refetch so callers that clear `isAnalyzing` before the backend has finished —
 * "stop generating", which clears it optimistically — can run it again once the stop resolves.
 */
export function useRefetchArtifactsOnSettle(isAnalyzing: boolean): () => void {
  const client = useApolloClient();
  const wasAnalyzingRef = useRef(false);

  const refetchArtifacts = useCallback(() => {
    void client.refetchQueries({ include: [namedOperations.Query.GetConversationArtifacts] });
  }, [client]);

  useEffect(() => {
    if (wasAnalyzingRef.current && !isAnalyzing) {
      refetchArtifacts();
    }
    wasAnalyzingRef.current = isAnalyzing;
  }, [isAnalyzing, refetchArtifacts]);

  return refetchArtifacts;
}
